import React, { type FunctionComponent, useCallback, useMemo, useState, useEffect } from 'react'
import ReactSelect, { type Styles } from 'react-select'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { StoreGroupFormModal } from 'domains/aggregate-store-performance/components/store-group-form-modal/StoreGroupFormModal'
import { EditStoreGroupsModal } from 'domains/aggregate-store-performance/components/edit-store-group-modal/EditStoreGroupsModal'
import {
  getDropdownStyles,
  DROPDOWN_WIDTH,
} from 'domains/aggregate-store-performance//utils/helpers/Dropdown.utils'
import { Menu } from './components/Menu'
import { Option } from './components/Option'
import {
  type ActionMetaWithOption,
  type StoreGroupDropdownOption,
} from './utils/StoreGroupDropdown.types'
import { ALL_STORES_OPTION_ID } from './utils/StoreGroupDropdown.constants'

const selectStyles: Styles = getDropdownStyles({
  container: () => ({
    width: DROPDOWN_WIDTH,
  }),
})

interface StoreGroupDropdownProps {
  storeGroups: StoreGroupDropdownOption[]
  selectedStoreGroups: StoreGroupDropdownOption[]
  onSelectionChange: (values: StoreGroupDropdownOption[]) => void
  refetchStoreGroups: () => void
  isLoading: boolean
}

export const StoreGroupDropdown: FunctionComponent<StoreGroupDropdownProps> = ({
  storeGroups = [],
  selectedStoreGroups = [],
  onSelectionChange,
  refetchStoreGroups,
  isLoading,
}) => {
  const messages = useDomainMessages({
    label: 'aggregateStorePerformanceDomain.storeGroupDropdown.label',
    defaultPlaceholder: 'aggregateStorePerformanceDomain.storeGroupDropdown.defaultPlaceholder',
    groupsSelectedPlaceholder:
      'aggregateStorePerformanceDomain.storeGroupDropdown.groupsSelectedPlaceholder',
    allStores: 'aggregateStorePerformanceDomain.storeGroupDropdown.allStores',
  })

  const [selection, setSelection] = useState([])
  const [selectionOnOpen, setSelectionOnOpen] = useState(selection)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isCreateStoreGroupModalOpen, setIsCreateStoreGroupModalOpen] = useState(false)
  const [isEditStoreGroupsModalOpen, setIsEditStoreGroupsModalOpen] = useState(false)
  const [editingStoreGroupId, setEditingStoreGroupId] = useState<string | undefined>(undefined)

  useEffect(() => {
    setSelection(selectedStoreGroups)
  }, [selectedStoreGroups])

  const [controlMessage, setControlMessage] = useState(messages.defaultPlaceholder)

  const options: StoreGroupDropdownOption[] = useMemo(() => [...storeGroups], [storeGroups])

  useEffect(() => {
    const message =
      selection.length > 0
        ? `${messages.groupsSelectedPlaceholder} (${selection.length})`
        : messages.defaultPlaceholder
    setControlMessage(message)
  }, [messages.defaultPlaceholder, messages.groupsSelectedPlaceholder, selection])

  const handleChange = useCallback(
    (values: StoreGroupDropdownOption[], actionMeta: ActionMetaWithOption) => {
      const updatedValues =
        actionMeta.action === 'select-option' && actionMeta.option.value === ALL_STORES_OPTION_ID
          ? values.filter(value => value.value === ALL_STORES_OPTION_ID)
          : values.filter(value => value.value !== ALL_STORES_OPTION_ID)
      setSelection(updatedValues)
    },
    []
  )

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const handleSave = useCallback(() => {
    onSelectionChange(selection)
    setSelectionOnOpen(selection)
    closeMenu()
  }, [closeMenu, onSelectionChange, selection])

  const handleClear = useCallback(() => {
    setSelection(storeGroups.filter(storeGroup => storeGroup.value === ALL_STORES_OPTION_ID))
  }, [storeGroups])

  const handleBlur = useCallback(() => {
    setSelection(selectionOnOpen)
    closeMenu()
  }, [closeMenu, selectionOnOpen])

  const handleOnOpenMenu = useCallback(() => {
    setIsMenuOpen(true)
    setSelectionOnOpen(selection)
  }, [selection])

  const openCreateStoreGroupModal = useCallback(() => {
    setIsCreateStoreGroupModalOpen(true)
  }, [])

  const closeCreateStoreGroupModal = useCallback(() => {
    refetchStoreGroups()
    setIsCreateStoreGroupModalOpen(false)
    setEditingStoreGroupId(undefined)
    setIsMenuOpen(true)
  }, [refetchStoreGroups])

  const openEditStoreGroupsModal = useCallback(() => {
    setIsEditStoreGroupsModalOpen(true)
  }, [])

  const closeEditStoreGroupsModal = useCallback(() => {
    refetchStoreGroups()
    setIsEditStoreGroupsModalOpen(false)
    setIsMenuOpen(true)
  }, [refetchStoreGroups])

  const handleEditStoreGroup = useCallback(
    (storeGroupId: string) => {
      setEditingStoreGroupId(storeGroupId)
      openCreateStoreGroupModal()
    },
    [openCreateStoreGroupModal]
  )

  return (
    <>
      <ReactSelect
        components={{ Menu, Option }}
        controlShouldRenderValue={false}
        isMulti
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        isSearchable={false}
        isClearable={false}
        isLoading={isLoading}
        name="storeGroupsDropdown"
        ariaLabel={messages.label}
        placeholder={controlMessage}
        value={selection}
        options={options}
        styles={selectStyles}
        menuIsOpen={isMenuOpen}
        onSave={handleSave}
        onClear={handleClear}
        onChange={handleChange}
        onMenuOpen={handleOnOpenMenu}
        onBlur={handleBlur}
        onCreateStoreGroupClicked={openCreateStoreGroupModal}
        onEditStoreGroupsClicked={openEditStoreGroupsModal}
      />
      {isCreateStoreGroupModalOpen && (
        <StoreGroupFormModal
          isOpen
          onClose={closeCreateStoreGroupModal}
          storeGroupId={editingStoreGroupId}
        />
      )}
      {isEditStoreGroupsModalOpen && (
        <EditStoreGroupsModal
          isOpen
          onClose={closeEditStoreGroupsModal}
          onEditClicked={handleEditStoreGroup}
          onAddNewClicked={openCreateStoreGroupModal}
        />
      )}
    </>
  )
}
